import { useEffect } from 'react'
import { supabase } from '../../lib/supabase'

export default function AuthCallback() {
  useEffect(() => {
    let handled = false

    // ── Password recovery ─────────────────────────────────
    // Hash tokens only exist for recovery links, not for Google OAuth
    const hashParams = new URLSearchParams(window.location.hash.slice(1))
    if (hashParams.get('type') === 'recovery') {
      const access  = hashParams.get('access_token')
      const refresh = hashParams.get('refresh_token')
      const finish  = () => { window.location.href = '/reset-password' }
      if (access && refresh) {
        supabase.auth.setSession({ access_token: access, refresh_token: refresh }).finally(finish)
      } else {
        finish()
      }
      return
    }

    // ── Route user to the right destination ───────────────
    async function routeUser(user) {
      if (handled) return
      handled = true

      try {
        // Partner invite join
        const joinToken = localStorage.getItem('okoiya_join_token')
        if (joinToken) {
          try {
            const { data } = await supabase.rpc('accept_partner_invite', { p_token: joinToken })
            if (data?.success) {
              localStorage.removeItem('okoiya_join_token')
              window.location.href = '/dashboard'
              return
            }
            localStorage.removeItem('okoiya_join_token')
          } catch(e) {}
        }

        // Check wedding + ambassador status
        // .catch on each query so a single DB failure never blocks routing
        const [weddingResult, ambassadorResult] = await Promise.all([
          supabase
            .from('weddings')
            .select('id')
            .or(`partner1_id.eq.${user.id},partner2_id.eq.${user.id}`)
            .catch(() => ({ data: [] })),
          supabase
            .from('asoebi_ambassadors')
            .select('id')
            .eq('email', user.email)
            .catch(() => ({ data: [] })),
        ])

        const weddings    = weddingResult?.data   || []
        const ambassadors = ambassadorResult?.data || []

        if (weddings.length + ambassadors.length > 1) {
          window.location.href = '/account-picker'
          return
        }
        window.location.href = weddings.length > 0 ? '/dashboard' : '/onboarding'

      } catch(err) {
        // Unexpected failure — never leave user stuck on the callback page
        window.location.href = '/login'
      }
    }

    // ── Auth state listener ───────────────────────────────
    // Listen to both SIGNED_IN and INITIAL_SESSION.
    // After exchangeCodeForSession completes, Supabase v2 may fire either
    // depending on the version and whether a previous session existed.
    // The `handled` flag ensures routeUser is only called once.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user && (event === 'SIGNED_IN' || event === 'INITIAL_SESSION')) {
        subscription.unsubscribe()
        routeUser(session.user)
      }
    })

    // ── PKCE code exchange ────────────────────────────────
    // Google OAuth redirects here with ?code=xxx
    // We exchange it for a session — this triggers the listener above.
    // The catch is SILENT — if exchange fails (e.g. code already processed
    // by Supabase's internal init), we let the listener handle the session.
    const code = new URLSearchParams(window.location.search).get('code')
    if (code) {
      supabase.auth.exchangeCodeForSession(code).catch(() => {})
    } else {
      // No code — implicit/hash flow or direct navigation
      // Poll until session appears
      ;(async () => {
        for (let i = 0; i < 10; i++) {
          const { data: { session } } = await supabase.auth.getSession()
          if (session?.user && !handled) { routeUser(session.user); return }
          await new Promise(r => setTimeout(r, 300))
        }
        if (!handled) {
          handled = true
          subscription.unsubscribe()
          window.location.href = '/login'
        }
      })()
    }

    // ── Hard timeout — never stay stuck ──────────────────
    const timeout = setTimeout(() => {
      if (!handled) {
        handled = true
        subscription.unsubscribe()
        window.location.href = '/login'
      }
    }, 10000)

    return () => {
      clearTimeout(timeout)
      try { subscription.unsubscribe() } catch(e) {}
    }
  }, [])

  return (
    <div style={{ minHeight:'100vh', background:'#FAFAF8', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:'1rem' }}>
      <div style={{ width:40, height:40, border:'3px solid #E5E0D8', borderTopColor:'#C4826A', borderRadius:'50%', animation:'spin 1s linear infinite' }}/>
      <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.875rem', color:'#9B9B9B', margin:0 }}>
        Setting things up…
      </p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}
